import React from 'react'
import { useSelector } from 'react-redux'
import { Formik } from 'formik'

import CustomStepEditor from 'components/Campaign/Editor/TaskEditor/CustomTaskEditor/StepsEditor'
import AttendEventPurchaseTaskEditor from 'components/Campaign/Editor/TaskEditor/PredefinedTaskEditor/AttendEventPurchaseTaskEditor'
import { TaskSections } from 'components/Campaign/Editor/TaskEditor'
import SocialMediaTaskEditor from 'components/Campaign/Editor/TaskEditor/PredefinedTaskEditor/SocialMediaTaskEditor'
import { TaskState } from 'state/tasks'
import { TASK_TYPE_ENUM } from 'services/api/constants'
import { selectSelectedCampaign } from 'state/review'
import { useStepEditorInitialValues } from 'hooks/steps'
import { PredefinedContext } from 'components/Campaign/Editor/TaskEditor/PredefinedTaskEditor'
import { getLinkInBioPostStepType } from 'components/utils'
import { selectTaskSetting, selectTaskStepSettings } from 'state/constants'
import { State } from 'state'

interface StepsEditorProps {
    task: TaskState
}

const ReadOnlyStepsEditor: React.FC<StepsEditorProps> = ({ task }) => {
    const campaign = useSelector(selectSelectedCampaign)
    const selectedTaskSetting = useSelector((state: State) =>
        selectTaskSetting(state, { taskType: task ? task.type : undefined }),
    )
    const stepSettings = useSelector((state: State) => selectTaskStepSettings(state, { task: task || undefined }))
    const initialValues = useStepEditorInitialValues(campaign, task)
    const linkInBioPostStepType = getLinkInBioPostStepType(task.type)
    const dynamicTaskState = {
        includedSteps: {
            submit_proof_photo: true,
        },
        additionalPayoutIds: [],
    }
    const allStepsInTask = task.steps.map(step => step.type)

    const getForm = () => {
        if (!campaign) return null
        switch (task.type) {
            case TASK_TYPE_ENUM.ATTEND_EVENT:
            case TASK_TYPE_ENUM.PURCHASE_PRODUCT: {
                return (
                    // provide context, since some step editor data is from use context PredefinedContext
                    <PredefinedContext.Provider
                        value={{
                            isCampaignLive: true,
                            linkInBioPostStepType,
                            task,
                            allStepsInTask,
                            stepSettings,
                            dynamicTaskState,
                            showSchedulePostTime: false,
                            isTaskWithEngagementStep: false,
                            // mock function, type is required, but not needed in readonly
                            addAdditionalPayout: () => '',
                            taskSettings: selectedTaskSetting,
                            // mock function, type is required, but not needed in readonly
                            removeAdditionalPayout: () => {},
                            campaignBrandCountry: campaign?.brand.country,
                        }}
                    >
                        <AttendEventPurchaseTaskEditor
                            task={task}
                            selectedTaskSection={TaskSections.STEPS}
                            requiredFieldNames={{}}
                            includedSteps={dynamicTaskState.includedSteps}
                            toggleIncludedStep={() => {}}
                            showBrief
                        />
                    </PredefinedContext.Provider>
                )
            }
            case TASK_TYPE_ENUM.IG_POST:
            case TASK_TYPE_ENUM.IG_POST_V02:
            case TASK_TYPE_ENUM.TIKTOK_POST:
            case TASK_TYPE_ENUM.FB_POST:
            case TASK_TYPE_ENUM.FB_POST_V02:
            case TASK_TYPE_ENUM.IG_STORY:
            case TASK_TYPE_ENUM.IG_STORY_V02:
            case TASK_TYPE_ENUM.IG_REEL:
                return (
                    // provide context, since some step editor data is from use context PredefinedContext
                    <PredefinedContext.Provider
                        value={{
                            isCampaignLive: true,
                            linkInBioPostStepType,
                            task,
                            allStepsInTask,
                            stepSettings,
                            dynamicTaskState,
                            showSchedulePostTime: true,
                            isTaskWithEngagementStep: true,
                            // mock function, type is required, but not needed in readonly
                            addAdditionalPayout: () => '',
                            taskSettings: selectedTaskSetting,
                            // mock function, type is required, but not needed in readonly
                            removeAdditionalPayout: () => {},
                            campaignBrandCountry: campaign?.brand.country,
                        }}
                    >
                        <SocialMediaTaskEditor
                            selectedTaskSection={TaskSections.STEPS}
                            task={task}
                            requiredFieldNames={{}}
                            showBrief
                        />
                    </PredefinedContext.Provider>
                )
            case TASK_TYPE_ENUM.CUSTOM: {
                return <CustomStepEditor onStepsChanged={() => {}} campaignStatus={campaign?.status} showBrief />
            }
            default: {
                return null
            }
        }
    }

    if (!initialValues) return null

    return (
        <Formik initialValues={initialValues} enableReinitialize onSubmit={() => {}}>
            {getForm()}
        </Formik>
    )
}

export default ReadOnlyStepsEditor
